/**
 * Trip File Setup Modal
 * Prompts user to set up auto-save for a new trip
 * Only shows if File System Access API is supported
 *
 * @version 1.0.0
 * @date 2025-11-13
 */

import React, { useState, useEffect } from 'react';
import tripFileManager from '../utils/tripFileManager';

const TripFileSetup = ({ tripId, tripData, onComplete, onSkip }) => {
  const [isSupported, setIsSupported] = useState(false);
  const [browserInfo, setBrowserInfo] = useState(null);
  const [setting, setSetting] = useState(false);

  useEffect(() => {
    const info = tripFileManager.getSupportInfo();
    setIsSupported(info.isSupported);
    setBrowserInfo(info);
  }, []);

  const handleSetupAutoSave = async () => {
    setSetting(true);

    try {
      const result = await tripFileManager.setupAutoSave(tripId, tripData);

      if (result.success) {
        alert(`✅ Auto-guardado configurado\n\n${result.fileName}\n\nTu viaje se guardará automáticamente cada vez que añadas fotos o notas.`);
        if (onComplete) onComplete(result);
      } else if (result.reason === 'user_cancelled') {
        // User cancelled, just close modal
        if (onSkip) onSkip();
      } else {
        alert(`❌ Error: ${result.message}`);
        if (onSkip) onSkip();
      }
    } catch (error) {
      alert(`❌ Error al configurar auto-guardado: ${error.message}`);
      if (onSkip) onSkip();
    } finally {
      setSetting(false);
    }
  };

  const handleSkip = () => {
    if (onSkip) onSkip();
  };

  // Don't show if not supported
  if (!isSupported) {
    return null;
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '1rem',
      backdropFilter: 'blur(4px)'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '20px',
        padding: '2rem',
        maxWidth: '550px',
        width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
        position: 'relative'
      }}>
        {/* Icon */}
        <div style={{
          fontSize: '4rem',
          textAlign: 'center',
          marginBottom: '1rem'
        }}>
          💾
        </div>

        {/* Title */}
        <h2 style={{
          color: '#8B7355',
          marginBottom: '1rem',
          textAlign: 'center',
          fontSize: '1.8rem',
          fontWeight: '900'
        }}>
          ¿Dónde Guardar tu Viaje?
        </h2>

        {/* Destination */}
        <p style={{
          color: '#666',
          marginBottom: '1.5rem',
          textAlign: 'center',
          fontSize: '1.1rem'
        }}>
          <strong>{tripData.destination?.city || 'Tu Viaje'}</strong>
          <br />
          <span style={{ fontSize: '0.9rem', color: '#999' }}>
            {tripData.dates?.startDate} - {tripData.dates?.endDate}
          </span>
        </p>

        {/* Info box */}
        <div style={{
          background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '1.5rem',
          borderLeft: '4px solid #4caf50'
        }}>
          <div style={{ marginBottom: '0.75rem', fontSize: '1.1rem' }}>
            <strong>✨ Auto-Guardado Inteligente</strong>
          </div>
          <ul style={{
            color: '#555',
            fontSize: '0.95rem',
            lineHeight: '1.8',
            margin: 0,
            paddingLeft: '1.5rem'
          }}>
            <li>Tu viaje se guarda <strong>automáticamente</strong> cada vez que añades fotos o notas</li>
            <li>Un <strong>solo archivo</strong> que se actualiza solo</li>
            <li>Guárdalo en <strong>Dropbox, Google Drive o iCloud</strong> para tener backup en la nube</li>
            <li>Formato especial <strong>.trip</strong> fácil de identificar</li>
          </ul>
        </div>

        {/* Recommendation box */}
        <div style={{
          background: '#fff3e0',
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '2rem',
          borderLeft: '4px solid #ff9800'
        }}>
          <div style={{ marginBottom: '0.5rem', fontSize: '1rem', color: '#f57c00' }}>
            <strong>💡 Recomendación:</strong>
          </div>
          <p style={{ color: '#555', fontSize: '0.9rem', lineHeight: '1.5', margin: 0 }}>
            Elige una carpeta <strong>sincronizada con la nube</strong> (Dropbox, Google Drive, iCloud Drive).
            Así tendrás backup automático sin ocupar espacio en nuestro servidor.
          </p>
        </div>

        {/* Buttons */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem'
        }}>
          <button
            onClick={handleSetupAutoSave}
            disabled={setting}
            style={{
              background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
              color: 'white',
              border: 'none',
              padding: '1.2rem',
              borderRadius: '12px',
              fontSize: '1.1rem',
              fontWeight: '700',
              cursor: setting ? 'not-allowed' : 'pointer',
              opacity: setting ? 0.6 : 1,
              transition: 'all 0.2s',
              boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
            onMouseEnter={(e) => {
              if (!setting) e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
            }}
          >
            {setting ? (
              <>
                <span>⏳</span>
                Configurando...
              </>
            ) : (
              <>
                <span>📁</span>
                Seleccionar Carpeta
              </>
            )}
          </button>

          <button
            onClick={handleSkip}
            disabled={setting}
            style={{
              background: 'transparent',
              color: '#999',
              border: 'none',
              padding: '0.8rem',
              fontSize: '0.9rem',
              cursor: setting ? 'not-allowed' : 'pointer',
              transition: 'color 0.2s'
            }}
            onMouseEnter={(e) => {
              if (!setting) e.target.style.color = '#e74c3c';
            }}
            onMouseLeave={(e) => {
              e.target.style.color = '#999';
            }}
          >
            Configurar más tarde
          </button>
        </div>

        {/* Browser info */}
        <p style={{
          fontSize: '0.85rem',
          color: '#999',
          textAlign: 'center',
          marginTop: '1.5rem',
          lineHeight: '1.5'
        }}>
          Usando {browserInfo?.browser}. Esta función solo funciona en Chrome y Edge.
          {!isSupported && <><br />Cambia a Chrome para habilitar auto-guardado.</>}
        </p>
      </div>
    </div>
  );
};

export default TripFileSetup;
